/**
 * runSprites — пиксельные инлайн-SVG спрайты run-вопроса (редизайн 0.14.1,
 * решение владельца 2026-10-20: «пиксельный человечек с руками и ногами» +
 * «более реалистичный дрон-квадрокоптер» + красивый взрыв).
 *
 * Выбор SVG (а не CSS box-shadow-пиксель-арта) — тестируемость в jsdom:
 * спрайт — обычное DOM-дерево <rect>, jsdom парсит его без моков canvas,
 * а shape-rendering="crispEdges" держит чёткие пиксели при любом масштабе.
 * Кадры ходьбы — 4 <g>-группы, переключаемых атрибутом data-frame на
 * контейнере (rAF мутирует атрибут напрямую, React не ре-рендерится);
 * кадры винтов дрона — чистая CSS-анимация steps(2).
 *
 * Все «случайные» формы (пятно, осколки) — детерминированные утилиты
 * runEngine (makeRng от diedAtMs, без новых RNG-источников): у игрока и
 * ведущего одинаковая картинка по одному моменту гибели.
 */
import type { CSSProperties } from 'react';
import { explosionShards, splatCells } from './runEngine';

/** Пиксель спрайта: <rect> с целочисленными координатами сетки 16×16. */
function px(
  x: number,
  y: number,
  w: number,
  h: number,
  fill: string,
  key?: string,
  dataAttrs?: Record<string, string>,
): JSX.Element {
  return (
    <rect key={key} x={x} y={y} width={w} height={h} fill={fill} {...(dataAttrs ?? {})} />
  );
}

// ─── Человечек (вид сверху, «лицом» вверх) ────────────────────────────────────

/**
 * Палитра статичных частей. 0.14.2 (замечание владельца после игротеста):
 * ноги/обувь — СВЕТЛЫЕ тона (тёмные штаны #1f2937 тонули в тёмной плитке —
 * «ноги не видны»); штаны и ботинки — разные светлые оттенки, чтобы шаг
 * читался. Кожа/волосы — как в 0.14.1; торс — цвет игрока (playerColor()).
 */
const SKIN = '#f2c49b';
const HAIR = '#3f2d20';
const HAIR_TOP = '#5b4331';
/** Штаны: тёплый светлый (контраст на тёмной плитке 0.14.1). */
export const PANTS_COLOR = '#ece7dd';
/** Ботинки: холодный светлый — отличим от штанов при шаге. */
export const BOOTS_COLOR = '#c7d3e2';

/**
 * Человечек с руками и ногами, вид сверху: голова с причёской (сверху),
 * торс (цвет игрока), руки по бокам, ноги — ПОВЕРХ торса (0.14.2: раньше
 * рисовались под ним и шаговые кадры перекрывались торсом — визуально «ноги
 * двигаются одновременно»; теперь каждая нога = светлые штаны + ботинок).
 * 4 кадра ходьба в ПРОТИВОФАЗЕ попеременно (0.14.2): 0 — левая нога вперёд,
 * 1 — нейтраль, 2 — правая вперёд, 3 — нейтраль; руки — противоположно ногам
 * (кадр 0: правая рука вперёд). idle = кадр 1 с дыханием (CSS-пульс группы).
 * Переключение кадров — селекторы `.run-player[data-frame]` в index.css;
 * поворот — transform: rotate на контейнере (rAF). data-leg/data-arm — опоры
 * тестов кадровой раскладки (не обязательны для рендера).
 */
export function PlayerSpriteSvg({ color }: { color: string }): JSX.Element {
  return (
    <svg
      className="run-player__sprite"
      viewBox="0 0 16 16"
      shapeRendering="crispEdges"
      aria-hidden="true"
    >
      {/* Руки (за торсом, по бокам): противофаза к ногам — вперёд идёт рука
          противоположной «вперёд»-ноге (кадры задают y: меньше = вперёд). */}
      <g className="run-pf run-pf--0">
        {px(2, 9, 2, 3, SKIN, 'a0-l', { 'data-arm': 'l' })}
        {px(12, 6, 2, 3, SKIN, 'a0-r', { 'data-arm': 'r' })}
      </g>
      <g className="run-pf run-pf--1">
        {px(2, 8, 2, 3, SKIN, 'a1-l', { 'data-arm': 'l' })}
        {px(12, 8, 2, 3, SKIN, 'a1-r', { 'data-arm': 'r' })}
      </g>
      <g className="run-pf run-pf--2">
        {px(2, 6, 2, 3, SKIN, 'a2-l', { 'data-arm': 'l' })}
        {px(12, 9, 2, 3, SKIN, 'a2-r', { 'data-arm': 'r' })}
      </g>
      <g className="run-pf run-pf--3">
        {px(2, 8, 2, 3, SKIN, 'a3-l', { 'data-arm': 'l' })}
        {px(12, 8, 2, 3, SKIN, 'a3-r', { 'data-arm': 'r' })}
      </g>

      {/* Статичные части: торс (цвет игрока) + голова с причёской сверху. */}
      <g className="run-player__body">
        {px(4, 8, 8, 4, color, 'torso')}
        {px(5, 8, 6, 1, 'rgba(255,255,255,0.28)', 'torso-hi')}
        {px(5, 4, 6, 4, SKIN, 'head')}
        {px(5, 2, 6, 2, HAIR, 'hair')}
        {px(5, 2, 6, 1, HAIR_TOP, 'hair-top')}
        {px(4, 3, 1, 2, HAIR, 'hair-l')}
        {px(11, 3, 1, 2, HAIR, 'hair-r')}
        {px(7, 5, 1, 1, 'rgba(0,0,0,0.25)', 'part')}
      </g>

      {/* Ноги ПОВЕРХ торса (0.14.2): штаны + ботинок, противофаза попеременно.
          Кадр 0 — левая вперёд (y меньше), правая назад; кадр 2 — зеркально;
          1/3 — нейтраль. «Вперёд» = вверх спрайта (курс), ботинок ниже штанов. */}
      <g className="run-pf run-pf--0">
        {px(5, 9, 2, 2, PANTS_COLOR, 'p0-l', { 'data-leg': 'l' })}
        {px(5, 11, 2, 2, BOOTS_COLOR, 'b0-l', { 'data-leg': 'l' })}
        {px(9, 12, 2, 2, PANTS_COLOR, 'p0-r', { 'data-leg': 'r' })}
        {px(9, 14, 2, 2, BOOTS_COLOR, 'b0-r', { 'data-leg': 'r' })}
      </g>
      <g className="run-pf run-pf--1">
        {px(5, 10, 2, 2, PANTS_COLOR, 'p1-l', { 'data-leg': 'l' })}
        {px(5, 12, 2, 2, BOOTS_COLOR, 'b1-l', { 'data-leg': 'l' })}
        {px(9, 10, 2, 2, PANTS_COLOR, 'p1-r', { 'data-leg': 'r' })}
        {px(9, 12, 2, 2, BOOTS_COLOR, 'b1-r', { 'data-leg': 'r' })}
      </g>
      <g className="run-pf run-pf--2">
        {px(9, 9, 2, 2, PANTS_COLOR, 'p2-r', { 'data-leg': 'r' })}
        {px(9, 11, 2, 2, BOOTS_COLOR, 'b2-r', { 'data-leg': 'r' })}
        {px(5, 12, 2, 2, PANTS_COLOR, 'p2-l', { 'data-leg': 'l' })}
        {px(5, 14, 2, 2, BOOTS_COLOR, 'b2-l', { 'data-leg': 'l' })}
      </g>
      <g className="run-pf run-pf--3">
        {px(5, 10, 2, 2, PANTS_COLOR, 'p3-l', { 'data-leg': 'l' })}
        {px(5, 12, 2, 2, BOOTS_COLOR, 'b3-l', { 'data-leg': 'l' })}
        {px(9, 10, 2, 2, PANTS_COLOR, 'p3-r', { 'data-leg': 'r' })}
        {px(9, 12, 2, 2, BOOTS_COLOR, 'b3-r', { 'data-leg': 'r' })}
      </g>
    </svg>
  );
}

// ─── Дрон-квадрокоптер (вид сверху, «носом» вверх) ────────────────────────────

const DRONE_BODY = '#334155';
const DRONE_BODY_HI = '#475569';
const DRONE_ARM = '#64748b';
const DRONE_MOTOR = '#94a3b8';
const DRONE_PROP = '#cbd5e1';
const DRONE_CAM = '#0f172a';

/**
 * Пиксельный квадрокоптер: корпус с камерой на носу, 4 луча-диагонали,
 * винты на моторах (2 кадра — мерцание вращения CSS steps(2), гасится
 * prefers-reduced-motion), лёгкий hover-боббинг (CSS translate на группе).
 * Курсовая ориентация — rotate контейнера по вектору полёта (rAF);
 * след выхлопа — .run-drone__trail в DOM дрона (позади по курсу).
 */
export function DroneSpriteSvg(): JSX.Element {
  const motors: Array<[number, number]> = [
    [1, 1],
    [12, 1],
    [1, 13],
    [12, 13],
  ];
  return (
    <svg
      className="run-drone__sprite"
      viewBox="0 0 16 16"
      shapeRendering="crispEdges"
      aria-hidden="true"
    >
      {/* Лучи-диагонали к моторам (пиксельные лесенки). */}
      {px(4, 5, 1, 1, DRONE_ARM, 'arm-0')}
      {px(3, 4, 1, 1, DRONE_ARM, 'arm-1')}
      {px(11, 5, 1, 1, DRONE_ARM, 'arm-2')}
      {px(12, 4, 1, 1, DRONE_ARM, 'arm-3')}
      {px(4, 10, 1, 1, DRONE_ARM, 'arm-4')}
      {px(3, 11, 1, 1, DRONE_ARM, 'arm-5')}
      {px(11, 10, 1, 1, DRONE_ARM, 'arm-6')}
      {px(12, 11, 1, 1, DRONE_ARM, 'arm-7')}

      {/* Моторы + винты: кадр A — полный диск, кадр B — половинный (CSS). */}
      {motors.map(([mx, my], i) => (
        <g key={`m-${i}`}>
          {px(mx, my, 3, 2, DRONE_MOTOR, `motor-${i}`)}
          <g className="run-drone__prop run-drone__prop--a">
            {px(mx - 1, my - 1, 5, 1, DRONE_PROP, `pa-${i}`)}
          </g>
          <g className="run-drone__prop run-drone__prop--b">
            {px(mx + 1, my - 1, 1, 1, DRONE_PROP, `pb-${i}`)}
          </g>
        </g>
      ))}

      {/* Корпус с кабиной и камерой на носу. */}
      <g className="run-drone__body">
        {px(5, 6, 6, 4, DRONE_BODY, 'body')}
        {px(6, 7, 4, 2, DRONE_BODY_HI, 'body-hi')}
        {px(7, 5, 2, 2, DRONE_CAM, 'cam')}
        {px(6, 9, 4, 1, '#22d3ee', 'cam-glow')}
      </g>
    </svg>
  );
}

// ─── Красное пятно гибели (пиксельная клякса) ─────────────────────────────────

/**
 * Пятно живёт TTL, затем плавно гаснет FADE и удаляется из DOM (0.14.2,
 * решение владельца главнее прежнего «остаётся до конца вопроса»: исчезает
 * по таймеру в ЛЮБОМ состоянии вопроса — активном или замороженном reveal).
 * reduced-motion — без fade-перехода, удаление тем же таймером.
 */
export const SPLAT_TTL_MS = 2800;
export const SPLAT_FADE_MS = 600;

/** Оттенки слоёв кляксы: тёмное ядро → капли → светлые брызги (0.14.2). */
const SPLAT_LAYER_COLORS = ['#7f1d1d', '#b91c1c', '#dc2626'] as const;

/**
 * Пятно на месте гибели: пиксельная клякса, форма детерминирована от
 * diedAtMs (splatCells — makeRng, без новых RNG-источников: одинаковая
 * клякса у игрока и ведущего). 0.14.2: три архетипа формы (клякса/шлейф/
 * кольцо) и послоевая окраска — ядро темнее, брызги светлее. Появление
 * лёгкое (гасится prefers-reduced-motion); исчезновение — по таймеру
 * вызывающего (SPLAT_TTL_MS/SPLAT_FADE_MS + класс run-splat--fade).
 * viewBox в нормализованных координатах — масштабируется с плиткой.
 */
export function SplatSvg({ diedAtMs }: { diedAtMs: number }): JSX.Element {
  const cells = splatCells(diedAtMs);
  return (
    <svg
      className="run-splat__svg"
      viewBox="-0.09 -0.09 0.18 0.18"
      shapeRendering="crispEdges"
      aria-hidden="true"
    >
      {cells.map((c, i) =>
        px(c.dx, c.dy, c.s, c.s, SPLAT_LAYER_COLORS[c.layer], `s-${i}`, {
          'data-layer': String(c.layer),
        }),
      )}
    </svg>
  );
}

// ─── Взрыв (вспышка + осколки + кольцо + дым) ─────────────────────────────────

/** Цвета осколков: бело-жёлто-оранжевая палитра (повторяется по кругу). */
const SHARD_COLORS = ['#fff7ed', '#fde047', '#fb923c'];

/**
 * Слои взрыва ~0.75 с: вспышка (белая → оранжевая), 10–16 пикселей-осколков
 * (физика «скорость + затухание» — CSS-easing к конечной точке; гравитации
 * нет — вид сверху), ударное кольцо, 2–3 серых пиксель-облачка дыма. Осколки
 * детерминированы diedAtMs (explosionShards). Масштаб — CSS-переменная
 * --ex (px на плитку; считает rAF-вызывающий), направления --sx/--sy.
 *
 * simple=true — упрощённый режим для канвы ведущего при >40 игроках:
 * вспышка + кольцо, без осколков/дыма (DOM не захлебнулся; CSS-класс
 * run-explosion--simple дополнительно скрывает узлы).
 */
export function ExplosionLayers({
  diedAtMs,
  simple = false,
}: {
  diedAtMs: number;
  simple?: boolean;
}): JSX.Element {
  const shards = simple ? [] : explosionShards(diedAtMs);
  const smoke: Array<[number, number, number]> = simple
    ? []
    : [
        [-0.06, -0.035, 0],
        [0.055, -0.02, 0.08],
        [0.02, 0.06, 0.16],
      ];
  return (
    <>
      <span className="run-explosion__flash" aria-hidden="true" />
      <span className="run-explosion__ring" aria-hidden="true" />
      {shards.map((s, i) => (
        <span
          key={`sh-${i}`}
          className="run-explosion__shard"
          style={
            {
              '--sx': `calc(var(--ex) * ${(s.dx * s.dist).toFixed(4)})`,
              '--sy': `calc(var(--ex) * ${(s.dy * s.dist).toFixed(4)})`,
              '--ss': `calc(var(--ex) * ${s.s.toFixed(4)})`,
              animationDelay: `${s.delay.toFixed(2)}s`,
              background: SHARD_COLORS[i % SHARD_COLORS.length],
            } as CSSProperties
          }
          aria-hidden="true"
        />
      ))}
      {smoke.map(([dx, dy, delay], i) => (
        <span
          key={`sm-${i}`}
          className="run-explosion__smoke"
          style={
            {
              '--sx': `calc(var(--ex) * ${dx.toFixed(3)})`,
              '--sy': `calc(var(--ex) * ${dy.toFixed(3)})`,
              animationDelay: `${delay}s`,
            } as CSSProperties
          }
          aria-hidden="true"
        />
      ))}
    </>
  );
}
